import { useState } from 'react';
import {
    Alert,
    Box,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GetConsentsResponse } from '@/models/company';
import { useFormatting } from '@/utils/formatting';
import {
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow
} from '@/components/shared/styles/Table';
import { TableCard } from '@/components/shared/Card';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import { PrimaryButton } from '@/components/shared/Button';
import { InstaconfigUploadDialog } from './InstaconfigUploadDialog';
import { useGetConsents } from '@/services/usersQuery';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import DeleteIcon from '@mui/icons-material/Delete';

export const LegalDocumentsTab = () => {
    const { t } = useTranslation();
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const { formatDateTime } = useFormatting();
    // const { data, error, isLoading } = useGetConsents();

    // useHandleResponse({ error });

    const consents = [] as any;

    const getStatusLabel = (consent: any) => {
        // Check if document is archived based on actionType or other fields
        if (consent.actionType === 'Declined') {
            return 'Archived';
        }
        return 'Active';
    };

    const getStatusColor = (status: string) => {
        return status === 'Active' ? 'success' : 'default';
    };

    return (
        <>
            <TableCard>
                <CardHeader
                    label={
                        <Stack direction={'row'} alignItems={'center'}>
                            {t('legal_documents')}
                        </Stack>
                    }
                />
                <TableContainer>
                    <Table
                        sx={{ minWidth: 650 }}
                        aria-label="legal documents table"
                    >
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    sx={{
                                        width: '45%'
                                    }}
                                >
                                    {t('document_column')}
                                </TableHeaderCell>
                                <TableHeaderCell>{t('status')}</TableHeaderCell>
                                <TableHeaderCell>
                                    {t('activated_at')}
                                </TableHeaderCell>
                                <TableHeaderCell align="right" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {consents.map((consent) => {
                                const status = getStatusLabel(consent);
                                const getDocumentName = () => {
                                    if (consent.termsVersionName) {
                                        return consent.termsVersionName;
                                    }
                                    switch (consent.consentType) {
                                        case 'Commercial':
                                            return t('license_agreement');
                                        case 'Data':
                                            return t(
                                                'data_processing_agreement'
                                            );
                                        case 'NewsletterAndUpdates':
                                            return t('marketing_updates');
                                        default:
                                            return consent.consentType;
                                    }
                                };

                                return (
                                    <TableRow key={consent.consentId}>
                                        <TableCell>
                                            {consent.termsVersionUrl ? (
                                                <a
                                                    href={
                                                        consent.termsVersionUrl
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{
                                                        color: '#1976d2'
                                                    }}
                                                >
                                                    {getDocumentName()}
                                                </a>
                                            ) : (
                                                getDocumentName()
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                color={getStatusColor(status)}
                                                label={status}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {formatDateTime(
                                                consent.performedAt
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {status === 'Archived' && (
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <PrimaryButton
                        onClick={() => setOpenUploadDialog(true)}
                        size="medium"
                    >
                        {t('upload_new_legal_document')}
                    </PrimaryButton>
                </Box>
            </TableCard>

            <InstaconfigUploadDialog
                open={openUploadDialog}
                onClose={() => setOpenUploadDialog(false)}
            />
        </>
    );
};
